import React, { useEffect, useRef, useState } from "react";
import { Wrapper, Image, Audio, Video } from "nystem-components";
import app from "nystem";
import "./view.css";

const makeUrl = ({ model, value, add, view, wait, tokenId: fixedTokenId }) => {
  const { id, ext, type } = value;
  let tokenId = false;
  let ready;
  if (model.secure) {
    tokenId = fixedTokenId || app.uuid();
    const { contentType } = view;
    ready = app.connection.emit({
      type: "secureFileToken",
      tokenId: `${tokenId}.${ext}`,
      fileId: `${id}.${ext}`,
      ext,
      contentType,
      modelId: model.id,
    });
  }
  const secure = model.secure ? "/secure" : "";
  const src = `/${type}${model.addToPath || ""}${secure}/${add || ""}${tokenId || id}.${ext}`;
  return wait ? Promise.resolve(ready).then(() => src) : src;
};

const AudioItem = ({ value, model, view, setValue }) => (
  <Audio
    className="w-full"
    preload={model.preload}
    controls={model.controls}
    src={makeUrl({ model, value, view })}
    type="audio/mpeg"
    playing={value.playing}
    currentTime={value.currentTime}
    updateInterval={model.updateInterval}
    onChange={(settings) => {
      if (setValue) setValue({ ...value, ...settings });
    }}
  />
);

const VideoItem = ({ value, model, view, setValue }) => {
  const [error, setError] = useState();
  const secure = Boolean(model.secure && app.session.user?.sessionid);
  const [src, setSrc] = useState();
  const [attempt, setAttempt] = useState(0);
  const [mp4Fallback, setMp4Fallback] = useState(false);
  const tokenId = useRef();
  const failed = useRef(false);
  const retries = useRef(0);
  const live = useRef({});
  const resume = useRef();
  // Snapshot position/state only when reloading, so ordinary re-renders never seek.
  const reload = () => { resume.current = { ...live.current }; setAttempt((attempt) => attempt + 1); };

  useEffect(() => {
    let mounted = true;
    setSrc(undefined);
    failed.current = false;
    tokenId.current = secure ? app.uuid() : undefined;
    makeUrl({ model: { ...model, secure }, value, view, wait: true, tokenId: tokenId.current }).then((src) => {
      if (mp4Fallback) src += "?format=mp4";
      if (mounted) setSrc(src);
    }).catch(() => { if (mounted) setError(true); });
    return () => { mounted = false; };
  }, [secure, model.addToPath, value.id, value.ext, value.type, view.contentType, model.id, attempt, mp4Fallback]);

  // Playback tokens are revoked when the connection drops. Re-register the same token on
  // reconnect so the playing element keeps working; reload if a request already failed.
  useEffect(() => {
    if (!secure) return;
    const onConnection = ({ connected }) => {
      if (!connected || !tokenId.current) return;
      makeUrl({ model: { ...model, secure }, value, view, wait: true, tokenId: tokenId.current })
        .then(() => { if (failed.current) reload(); })
        .catch(() => {});
    };
    app.connection.on("connection", onConnection);
    return () => app.connection.off("connection", onConnection);
  }, [secure, model.addToPath, value.id, value.ext, value.type, view.contentType, model.id]);

  if (error) return <AttachmentItem value={value} model={model} view={view} />;
  if (!src) return null;

  return (
    <Video
      className={model.videoClassName || model.imageClassName || model.itemClassName || "w-full object-contain"}
      preload={model.preload || "metadata"}
      controls={model.controls}
      src={src}
      playing={value.playing}
      currentTime={value.currentTime}
      resume={resume.current}
      updateInterval={model.updateInterval}
      onChange={(settings) => {
        if (settings.error) {
          // Decode/format errors need a different container; network errors keep token recovery.
          // Offline, a failed first load also reports "not supported", so it is a network error then.
          if ([3, 4].includes(settings.error.code) && navigator.onLine) {
            if (mp4Fallback || !/^(mov|m4v)$/i.test(value.ext)) return setError(true);
            resume.current = { ...live.current };
            retries.current = 0;
            setMp4Fallback(true);
            return;
          }
          if (retries.current >= 2) return setError(true);
          failed.current = true;
          retries.current++;
          // Without a connection the reconnect handler reloads; otherwise retry with a fresh token now.
          if (secure) {
            if (app.connection.connected !== false) reload();
            return;
          }
          // Public videos do not use the socket, so wait for the network or give it a moment.
          if (!navigator.onLine) window.addEventListener("online", reload, { once: true });
          else setTimeout(reload, 2000 * retries.current);
          return;
        }
        if (settings.playing) retries.current = 0;
        resume.current = undefined;
        if (settings.currentTime !== undefined) live.current.currentTime = settings.currentTime;
        if (settings.playing !== undefined) live.current.playing = settings.playing;
        if (setValue) setValue({ ...value, ...settings });
      }}
    />
  );
};

const ImageItem = ({ value, model, view, index }) => {
  const { width = 10, height = 10 } = app.image.calculateSize(value, model);

  const add = value.ext === "svg" || model.fullSize ? "" : `${width}x${height}/`;
  const style = model.fullSize ? { width: `${value.width}px`, height: `${value.height}px` } : {};

  const image = (
    <Image
      secure={model.secure}
      cache={model.cache}
      contentType={view.contentType}
      fieldId={model.id}
      width={width}
      height={height}
      originalWidth={value.width}
      originalHeight={value.height}
      fixed={model.fixed}
      src={`/image${model.addToPath || ""}/${add}${value.id}.${value.ext}`}
      className={[!model.slideshowOnClick && model.itemClassName, model.imageClassName || "object-contain"]}
      alt={value.name}
      limit={model.fullImageLimit}
      style={style}
      draggable={!!model.draggable}
    />
  );
  return model.slideshowOnClick ? (
    <Wrapper className={model.itemClassName} onClick={() => view.event("slideShow", { model, index })}>
      {image}
    </Wrapper>
  ) : (
    image
  );
};

const AttachmentItem = ({ value, model, view }) => (
  <Wrapper
    renderAs="a"
    className={[model.itemClassName, "p-3"]}
    href={`${makeUrl({ model, value, view })}?name=${value.name}`}
    target="_blank"
  >
    {value.name || model.text}
  </Wrapper>
);

const componentByType = { audio: AudioItem, image: ImageItem, video: VideoItem, attachment: AttachmentItem };

const MediaView = ({ model, view, setValue, value }) => {
  value = value instanceof Array ? value : [value];
  if (!value[0]) value = [];

  return (
    <Wrapper className={model.className}>
      {value.map((value, index) => {
        const type = ["audio", "image", "video"].includes(value.type) ? value.type : "attachment";

        const Component = type === "video" && model.thumbnail ? ImageItem : componentByType[type];
        return <Component key={value.id || index} index={index} value={value} model={model} view={view} setValue={setValue} />;
      })}
    </Wrapper>
  );
};

export default MediaView;
